import { Dispatch, useCallback } from 'react'
import { toast } from 'react-toastify'
import { validateEmail } from 'src/services/config/confirmeEmail'
import { validatePhone } from 'src/services/config/confirmePhone'
import { getAuthErrorMessage, getValidationErrorMessage } from 'src/constants/errorMessages'
import { LOGIN_STATUS, STORAGE_KEYS } from '../constants'
import { AuthAction } from '../reducers/auth.reducer'

interface UseConfirmationProps {
  dispatch: Dispatch<AuthAction>
  username?: string
}

export function useConfirmation({ dispatch, username }: UseConfirmationProps) {
  const validationPhone = useCallback(
    async (codigo: string) => {
      try {
        if (!codigo || codigo.length < 4) {
          const error = new Error('Código de verificação inválido')
          toast.error(getValidationErrorMessage(error))
          return
        }

        if (!username) {
          const error = new Error('Usuário não identificado')
          toast.error(getValidationErrorMessage(error))
          return
        }

        const response = await validatePhone(username, codigo)

        if (response) {
          localStorage.setItem(STORAGE_KEYS.PHONE_IS_CONFIRMED, 'true')
          dispatch({
            type: 'SET_CONFIRMATION_STATUS',
            payload: { phoneConfimed: 'true' }
          })
          toast.success('Telefone validado com sucesso!')
        } else {
          const error = new Error('Código de verificação incorreto')
          toast.error(getValidationErrorMessage(error))
        }
      } catch (error: any) {
        toast.error(getAuthErrorMessage(error))
      }
    },
    [dispatch, username]
  )

  const validationEmail = useCallback(
    async (codigo: string) => {
      try {
        if (!codigo || codigo.length < 4) {
          const error = new Error('Código de verificação inválido')
          toast.error(getValidationErrorMessage(error))
          return
        }

        if (!username) {
          const error = new Error('Usuário não identificado')
          toast.error(getValidationErrorMessage(error))
          return
        }

        const response = await validateEmail(username, codigo)

        if (response) {
          localStorage.setItem(STORAGE_KEYS.EMAIL_IS_CONFIRMED, 'true')
          dispatch({
            type: 'SET_CONFIRMATION_STATUS',
            payload: { emailConfimed: 'true' }
          })
          toast.success('E-mail validado com sucesso!')
        } else {
          const error = new Error('Código de verificação incorreto')
          toast.error(getValidationErrorMessage(error))
        }
      } catch (error: any) {
        toast.error(getAuthErrorMessage(error))
      }
    },
    [dispatch, username]
  )

  const resendPhoneCode = useCallback(async () => {
    try {
      if (!username) {
        const error = new Error('Usuário não identificado')
        toast.error(getValidationErrorMessage(error))
        return
      }

      dispatch({ type: 'SET_STATUS_LOGIN', payload: LOGIN_STATUS.RESENDING_CODE })
      toast.success('Código reenviado com sucesso!')
      dispatch({ type: 'SET_STATUS_LOGIN', payload: '' })
    } catch (error: any) {
      toast.error(getAuthErrorMessage(error))
      dispatch({ type: 'SET_STATUS_LOGIN', payload: '' })
    }
  }, [dispatch, username])

  const resendEmailCode = useCallback(async () => {
    try {
      if (!username) {
        const error = new Error('Usuário não identificado')
        toast.error(getValidationErrorMessage(error))
        return
      }

      dispatch({ type: 'SET_STATUS_LOGIN', payload: LOGIN_STATUS.RESENDING_CODE })
      toast.success('Código reenviado com sucesso!')
      dispatch({ type: 'SET_STATUS_LOGIN', payload: '' })
    } catch (error: any) {
      toast.error(getAuthErrorMessage(error))
      dispatch({ type: 'SET_STATUS_LOGIN', payload: '' })
    }
  }, [dispatch, username])

  return {
    validationPhone,
    validationEmail,
    resendPhoneCode,
    resendEmailCode
  }
}
